/**
 * 웹 쪽 선언과 그 판정기 — G7 `pnpm web:check` 의 순수 부분. (docs/decisions/0008 §구현 순서 9)
 *
 * ─── 왜 판정기가 화면 코드보다 먼저인가 ───
 * 0008 이 세 설계안 중 `enforcement-first` 를 채택한 제1원리가 이것이다:
 * **먼저 판정기를 정하고, 판정기가 볼 수 있는 것만 확장점에 넣는다.** 0007 이
 * "`os` 를 export 하지 않는다"를 먼저 정하고 API 모양을 결과로 얻은 것과 같은 순서다.
 * 그래서 이 파일은 `packages/core-web` 도 `apps/web` 도 없는 시점에 먼저 들어간다 —
 * 그 둘이 무엇을 내놓아야 하는지를 여기가 정한다.
 *
 * ─── 무엇이 어긋날 수 있는가 (이 파일이 막는 것) ───
 * `surface.ts` 는 **선언**을 본다. 선언이 맞아도 웹에는 두 번째 사실이 남는다:
 * 그 화면의 코드가 실제로 존재하고 로더에 연결돼 있는가. 그 둘이 어긋나는 두 방향이
 * 구 시스템에 **둘 다** 있었다:
 *  - 라우트는 있는데 뷰가 없다 → 클릭하면 빈 화면. 에러 0, 콘솔 0.
 *  - 뷰는 있는데 라우트가 없다 → `pdp.routes.ts` 가 `export const pdpRoutes = []` 3줄인데
 *    `views/pdp` 4화면 · `components/pdp` 33개 · `ko.ts` 의 pdp 321줄이 그대로 남아 있었다.
 * **양방향 대조(used/orphan)** 는 그래서 한 쌍이다. 한 방향만 보면 죽은 코드가 영원히 산다.
 *
 * ─── 이 파일은 react-router 도 React 도 모른다 ───
 * `.dependency-cruiser.cjs` 의 `react-router-only-in-core-web-and-sdk` 가 게이트(`apps/tools`)를
 * 포함해 core-web·web-sdk 밖 전부를 막는다. 그래서 **게이트는 `RouteObject` 를 볼 수 없다.**
 * 대신 `core-web` 이 감사 투영(`RouteAuditRow[]`)을 내놓고 게이트는 그것을 읽는다 —
 * 라우터 라이브러리를 아는 곳이 하나로 유지되면서도 감사가 가능해지는 유일한 형태다.
 * (같은 이유로 `ScreenLoader` 의 반환은 `unknown` 이다. React 컴포넌트 타입은 core-web 이 좁힌다.)
 *
 * ─── 여기 없는 것 ───
 * **native 표면(G7 "native ⊆ web")** 은 아직 필드조차 만들지 않았다. 모바일 앱이 0개라
 * 지금 만들면 검사 대상이 0개인 게이트가 되고, 그건 `audit.ts:162-166`("검사 대상 0개는
 * 통과가 아니라 무력화")이 금지한 상태 그 자체다. 게이트는 이 사실을 **매번 출력한다** —
 * 조용히 건너뛰는 검사는 없는 검사와 같다.
 */
import type { SurfaceManifest, SurfaceModule, SurfaceProblem } from './surface.js';
import { screensOf, toSurfaceCatalog } from './surface.js';

/**
 * RPC 마운트 경로 — **클라이언트와 서버가 합의해야 하는 유일한 URL 이다.**
 *
 * 여기 사는 이유: 이건 타입이 아니라 값이고, `apps/web → apps/server` 통로는
 * **type-only 하나뿐**이다(`web-must-not-import-server` 의 `dependencyTypesNot`).
 * 그래서 값으로 공유할 수 있는 자리가 contracts 밖에 없다. 서버가 `'/rpc'` 를 자기 파일에
 * 적고 웹이 또 적으면 그건 같은 사실의 사본 둘이고, 프리픽스를 옮기는 날 한쪽만 바뀐다 —
 * 그때 증상은 에러가 아니라 **전 요청 404** 다.
 *
 * G7 검사 8 이 이 상수 말고 다른 곳에 `'/rpc'` 리터럴이 있으면 실패한다.
 * **스캔이 못 보는 사본이 하나 있다**: `scripts/smoke-api.sh` 는 셸이라 이 값을 import 할 수
 * 없다. 그건 실제 소켓을 때리는 외부 클라이언트라 어차피 계약의 반대편이고, 어긋나면
 * 스모크가 즉시 빨개진다(조용하지 않다) — 그래서 감수하고 여기 적어 둔다.
 */
export const RPC_PREFIX = '/rpc';

/**
 * 화면 청크 로더. `() => import('./screens/Runs.js')` 한 줄이다.
 *
 * 반환이 `unknown` 인 이유는 위 "이 파일은 React 를 모른다" 다. 이걸 좁히는 곳은
 * `packages/core-web` 이고, 좁히는 것은 그 패키지 하나로 충분하다 —
 * vertical 은 로더를 **적기만** 하고 그 결과 타입을 쓰지 않는다.
 */
export type ScreenLoader = () => Promise<unknown>;

/**
 * 모듈의 웹 쪽 절반. `ModuleSurface` 와 **같은 모양의 배타 유니온**이다 —
 * 화면이 없다는 것도 적어야 하고(`headless`), 빈 객체로 표현할 수 없다.
 *
 * 왜 `screens` 가 배열이 아니라 맵인가: 키가 곧 `ScreenDef.key` 이고 그 대조가 이 파일의
 * 본론이다. 배열이면 같은 화면 키에 로더 둘을 적을 수 있고, 그 상태에서 이기는 쪽은
 * 선언 순서다 — 에러 없이 화면이 바뀐다.
 */
export type WebModuleDef =
  | {
      readonly key: string;
      readonly screens: Readonly<Record<string, ScreenLoader>>;
      readonly headless?: never;
    }
  | { readonly key: string; readonly headless: string; readonly screens?: never };

/**
 * `apps/web/src/modules/registry.ts` 의 리터럴 맵. **vertical 을 나열하는 유일한 웹 파일**이다.
 *
 * 값이 dynamic import 인 것이 청크 경계다 — 스테이션온을 안 쓰는 테넌트는 그 청크를
 * fetch 조차 하지 않는다. 다만 **모듈 키 문자열은 엔트리 번들에 남는다**(Rollup 정적 분석의
 * 대가). 계획서 §4 의 "메타데이터까지 fetch 안 한다"는 JS 청크에만 참이다(0008 §감수한 것).
 */
export type WebModuleLoader = () => Promise<{ readonly default: WebModuleDef }>;
export type WebRegistry = Readonly<Record<string, WebModuleLoader>>;

/** 레지스트리에서 실제로 로드해 온 것. 게이트가 `await` 한 뒤의 모양이다. */
export interface LoadedWebModule {
  /** 레지스트리 맵의 키. `def.key` 와 다를 수 있고, 그 불일치가 검사 대상이다. */
  readonly registryKey: string;
  readonly def: WebModuleDef;
}

/**
 * 라우트 감사 투영 — `core-web` 이 `webBuildCheck()` 로 내놓고 게이트가 읽는다.
 *
 * `RouteObject` 를 그대로 노출하지 않는 이유가 둘이다: ① 게이트는 react-router 를
 * import 할 수 없다(G5) ② 그대로 노출하면 게이트가 라우터 구현 세부에 묶여 버전 업마다
 * 깨진다. **감사에 필요한 사실만** 남긴다.
 */
export type RouteAuditKind = 'screen' | 'shell' | 'landing' | 'notFound';

export interface RouteAuditRow {
  readonly kind: RouteAuditKind;
  /** 절대 경로. 화면 라우트는 `absolutePathOf()` 의 출력과 **문자 그대로** 같아야 한다. */
  readonly path: string;
  /** 화면 라우트만 값이 있다. 구조 라우트(`shell`·`landing`·`notFound`)는 null 이다. */
  readonly screenKey: string | null;
  /** 라우트 트리에서의 깊이. 셸이 0, 화면이 1. **2 이상은 중첩이고 그건 거부한다.** */
  readonly depth: number;
}

/**
 * `buildRoutes` 가 만든 것임을 표시하는 브랜드. `webBuildCheck` 가 이걸 확인한다.
 *
 * **인가가 아니라 일관성 표시다.** 누구든 `Symbol.for(...)` 를 직접 찍을 수 있다 —
 * 서버 브랜드도 마찬가지지만 거긴 보안 경계의 증거이고 여기는 아니다.
 * 이 구분을 안 적으면 다음 사람이 웹 가드를 보안 경계로 믿는다(0008 §웹 게이트의 성질).
 */
export const WEB_ROUTE_BRAND = Symbol.for('worhythm.web.routes');

/* ────────────────────────────────────────────────────────────────────────── */

const problem = (where: string, reason: string): SurfaceProblem => ({ where, reason });

/**
 * 검사 1·2 — 모듈 강제 해소 + 화면 키 양방향 대조.
 *
 * "강제 해소"는 **화면을 선언한 모듈 집합과 레지스트리 키 집합이 정확히 같다**는 뜻이다.
 * 한쪽 포함이 아니라 상등이어야 하는 이유: headless 모듈에 레지스트리 항목이 있으면
 * 아무도 안 여는 청크가 빌드에 남고, 그 청크는 **삭제할 이유가 문서에만 있는 코드**가 된다.
 *
 * 던지지 않는다 — 호출자가 전부 모아 한 번에 보고할 수 있게(`checkSurface` 와 같은 규약).
 */
export function checkWebRegistry(input: {
  readonly modules: readonly SurfaceModule[];
  readonly loaded: readonly LoadedWebModule[];
}): SurfaceProblem[] {
  const problems: SurfaceProblem[] = [];
  const { modules, loaded } = input;

  if (modules.length === 0) {
    return [
      problem(
        '(표면 카탈로그 전체)',
        '판정할 모듈이 0개다. 조립이 깨졌다 — 빈 입력에 초록을 주지 않는다. ' +
          '검사 대상이 0개인 게이트는 통과가 아니라 무력화다.',
      ),
    ];
  }

  const byRegistryKey = new Map<string, LoadedWebModule>();
  for (const w of loaded) {
    if (byRegistryKey.has(w.registryKey)) {
      // JS 객체 리터럴에서는 일어날 수 없지만, 레지스트리가 코드로 조립되는 날 생긴다.
      problems.push(problem(`registry['${w.registryKey}']`, `레지스트리 키가 중복됐다.`));
      continue;
    }
    byRegistryKey.set(w.registryKey, w);
  }

  const declaredModuleKeys = new Set(modules.map((m) => m.key));

  for (const m of modules) {
    const screens = screensOf(m.surface);
    const web = byRegistryKey.get(m.key);
    const headlessReason = m.surface.headless;

    if (screens.length === 0) {
      /* headless 모듈 — 레지스트리에 있으면 안 된다. */
      if (web) {
        problems.push(
          problem(
            `registry['${m.key}']`,
            `모듈 '${m.key}' 는 headless 인데(${JSON.stringify(headlessReason)}) 레지스트리에 항목이 있다. ` +
              `아무도 열지 않는 청크가 빌드에 남는다 — 지우거나, 화면을 선언한다.`,
          ),
        );
      }
      continue;
    }

    if (!web) {
      problems.push(
        problem(
          `registry['${m.key}']`,
          `모듈 '${m.key}' 가 화면 ${screens.length}개를 선언했는데 웹 레지스트리에 없다. ` +
            `라우트가 만들어지지 않으므로 그 화면들은 **영원히 안 열린다** — 서버의 403 과 달리 ` +
            `에러도 로그도 없다. 다음 행동: apps/web/src/modules/registry.ts 에 ` +
            `'${m.key}': () => import('<모듈의 web 진입점>') 를 한 줄 추가한다.`,
        ),
      );
      continue;
    }

    if (web.def.key !== m.key) {
      problems.push(
        problem(
          `registry['${m.key}'].key`,
          `레지스트리 키는 '${m.key}' 인데 로드된 모듈이 스스로를 '${web.def.key}' 라고 한다. ` +
            `둘 중 어느 쪽이 진짜인지 코드가 말하지 못하는 상태다.`,
        ),
      );
      continue;
    }

    if (web.def.screens === undefined) {
      problems.push(
        problem(
          `registry['${m.key}']`,
          `표면은 화면 ${screens.length}개를 선언했는데 웹 모듈은 headless 라고 한다` +
            `(${JSON.stringify(web.def.headless)}). 같은 사실의 두 사본이 이미 어긋났다.`,
        ),
      );
      continue;
    }

    /* ── 화면 키 양방향 대조 ── */
    const declared = new Set(screens.map((s) => s.key));
    const wired = new Set(Object.keys(web.def.screens));

    for (const key of declared) {
      if (!wired.has(key)) {
        problems.push(
          problem(
            `registry['${m.key}'].screens['${key}']`,
            `화면 '${key}' 가 선언됐는데 로더가 없다. 라우트는 만들어지고 뷰는 없다 — ` +
              `클릭하면 빈 화면이고 에러도 콘솔도 없다. ` +
              `다음 행동: 그 모듈의 web 진입점 로더 맵에 '${key}': () => import('...') 를 더한다.`,
          ),
        );
      }
    }
    for (const key of wired) {
      if (!declared.has(key)) {
        problems.push(
          problem(
            `registry['${m.key}'].screens['${key}']`,
            `로더 '${key}' 에 대응하는 화면 선언이 없다. 도달 불가능한 코드가 번들에 남는다 — ` +
              `구 시스템의 pdp 가 이 형태였다(라우트 0개인데 뷰 4개·컴포넌트 33개·로케일 321줄). ` +
              `다음 행동: 모듈 surface 에 ScreenDef 를 선언하거나 로더를 지운다.`,
          ),
        );
      }
      const loader = (web.def.screens as Record<string, unknown>)[key];
      if (typeof loader !== 'function') {
        problems.push(
          problem(
            `registry['${m.key}'].screens['${key}']`,
            `로더가 함수가 아니다(${typeof loader}). 값을 직접 넣으면 그 화면의 코드가 ` +
              `엔트리 번들로 끌려 들어와 청크 경계가 조용히 사라진다.`,
          ),
        );
      }
    }
  }

  /* ── 레지스트리에만 있는 모듈 ── */
  for (const key of byRegistryKey.keys()) {
    if (!declaredModuleKeys.has(key)) {
      problems.push(
        problem(
          `registry['${key}']`,
          `레지스트리에 있는 모듈 '${key}' 가 표면 카탈로그에 없다. composition root 에서 빠졌거나 ` +
            `이름이 바뀐 것이다 — 어느 쪽이든 그 청크는 아무도 열지 않는다.`,
        ),
      );
    }
  }

  return problems;
}

/**
 * 검사 3 — `requires` ⊆ RBAC 카탈로그. **조립된 뒤에만 할 수 있는 검사다.**
 *
 * `checkSurface` 는 모듈 하나만 보므로 `owner === mod.key` 일 때만 판정한다.
 * 다른 모듈의 permission 을 요구하는 화면(vertical 화면이 `core.*` 를 요구하는 형태)은
 * 거기서 **원리적으로** 못 본다 — 등록 순서에 의존하는 검사를 register 시점에 넣으면
 * 모듈 순서가 곧 배선이 되기 때문이다. 그 나머지 절반을 여기서 본다.
 *
 * 오타의 증상이 서버와 웹에서 다르다: 서버는 403(시끄럽다), 웹은 **영원히 안 보이는 메뉴**다.
 */
export function checkScreenPermissions(
  modules: readonly SurfaceModule[],
  permissionCatalog: ReadonlySet<string>,
): SurfaceProblem[] {
  const problems: SurfaceProblem[] = [];

  if (permissionCatalog.size === 0) {
    return [
      problem(
        '(permission 카탈로그 전체)',
        'permission 카탈로그가 비었다. 이 상태로는 모든 requires 가 "없는 키"가 되거나 ' +
          '검사가 통째로 공허해진다 — 판정 대상이 0개인 검사는 통과가 아니다.',
      ),
    ];
  }

  for (const e of toSurfaceCatalog(modules)) {
    if (e.screen.kind !== 'permission') continue;
    if (permissionCatalog.has(e.screen.requires)) continue;
    problems.push(
      problem(
        `${e.moduleKey}.screens[${e.screen.key}].requires`,
        `requires '${e.screen.requires}' 가 조립된 RBAC 카탈로그에 없다. ` +
          `다음 행동: 그 키를 소유한 모듈의 RbacManifest 에 permission 을 선언하고 ` +
          `\`pnpm rbac:sync\` 를 돌린다. 이걸 놓치면 서버에서는 403 이지만 ` +
          `웹에서는 "영원히 안 보이는 메뉴"라 더 조용하다.`,
      ),
    );
  }

  return problems;
}

/**
 * 검사 5·7 — 라우트·내비 일치 + 라우트 0개 거부.
 *
 * 판정하는 것은 **`buildRoutes` 와 `deriveNav` 가 같은 선언을 같게 읽었는가**다.
 * 0008 이 `route-first` 안을 탈락시킨 fatal 이 정확히 이 지점이었다: 원본은 하나인데
 * 해석이 둘이고, 어긋남이 **권한이 넓어지는 쪽**이며 로그도 메트릭도 안 남는다.
 * 해석이 둘이라는 사실 자체는 못 없앤다(라우터는 라우터가 만들어야 한다) — 그래서 매 빌드마다 대조한다.
 *
 * 입력 매니페스트는 **전 권한을 준 상태**여야 한다. granted 를 좁힌 매니페스트로 이 검사를
 * 돌리면 "권한이 없어 빠진 화면"과 "로더가 없어 빠진 화면"이 같은 모양이 된다.
 */
export function checkRouteAudit(input: {
  readonly rows: readonly RouteAuditRow[];
  readonly manifest: SurfaceManifest;
  /** `deriveNav(manifest)` 의 화면 키. 내비가 라우트의 부분집합인지 본다. */
  readonly navScreenKeys: readonly string[];
}): SurfaceProblem[] {
  const problems: SurfaceProblem[] = [];
  const { rows, manifest, navScreenKeys } = input;

  if (rows.length === 0) {
    return [
      problem(
        '(라우트 전체)',
        '라우트를 하나도 찾지 못했다. 라우터가 비면 앱은 **에러 없이** 뜨고 모든 경로가 ' +
          'NotFound 가 된다 — 그 상태는 "아직 안 만들었다"와 구분되지 않는다.',
      ),
    ];
  }

  /* ── 구조 라우트는 정확히 하나씩 ── */
  for (const kind of ['shell', 'landing', 'notFound'] as const) {
    const n = rows.filter((r) => r.kind === kind).length;
    if (n !== 1) {
      problems.push(
        problem(
          `routes[kind=${kind}]`,
          `구조 라우트 '${kind}' 가 ${n}개다. 정확히 1개여야 한다 — 0개면 그 경로가 죽고, ` +
            `2개 이상이면 이기는 쪽이 선언 순서로 정해진다.`,
        ),
      );
    }
  }

  /* ── 깊이는 정확히 2 (셸 0 · 나머지 1) ── */
  for (const r of rows) {
    const expected = r.kind === 'shell' ? 0 : 1;
    if (r.depth !== expected) {
      problems.push(
        problem(
          `routes['${r.path}'].depth`,
          `깊이가 ${r.depth} 다(기대 ${expected}). 라우트를 중첩하면 매칭된 브랜치의 **모든** ` +
            `loader 가 돌아 선언되지 않은 permission 논리곱이 생긴다 — 0008 이 route-first 를 ` +
            `탈락시킨 fatal 이다. 트리 깊이는 정확히 2 다.`,
        ),
      );
    }
  }

  /* ── 화면 라우트 ↔ 매니페스트 양방향 ── */
  const routed = new Map<string, RouteAuditRow>();
  for (const r of rows) {
    if (r.kind !== 'screen') {
      if (r.screenKey !== null) {
        problems.push(
          problem(
            `routes['${r.path}'].screenKey`,
            `구조 라우트 '${r.kind}' 에 screenKey '${r.screenKey}' 가 붙었다. ` +
              `구조 라우트는 선언되지 않은 코어 소유 라우트이므로 화면 키를 가질 수 없다.`,
          ),
        );
      }
      continue;
    }
    if (r.screenKey === null) {
      problems.push(problem(`routes['${r.path}']`, `화면 라우트에 screenKey 가 없다. 감사가 이 URL 의 주인을 모른다.`));
      continue;
    }
    if (routed.has(r.screenKey)) {
      problems.push(problem(`routes['${r.path}']`, `화면 '${r.screenKey}' 에 라우트가 둘이다. 하나는 영원히 안 열린다.`));
      continue;
    }
    routed.set(r.screenKey, r);
  }

  const manifestByKey = new Map(manifest.screens.map((s) => [s.key, s]));

  for (const s of manifest.screens) {
    const r = routed.get(s.key);
    if (!r) {
      problems.push(
        problem(
          `routes['${s.key}']`,
          `매니페스트에 있는 화면 '${s.key}' 에 라우트가 없다. 메뉴에는 뜨는데 열면 NotFound 다.`,
        ),
      );
      continue;
    }
    if (r.path !== s.path) {
      problems.push(
        problem(
          `routes['${s.key}'].path`,
          `라우트 경로 '${r.path}' 가 매니페스트의 '${s.path}' 와 다르다. ` +
            `경로를 만드는 자리는 absolutePathOf() 하나여야 한다 — 문자열을 다시 조립하면 여기서 갈린다.`,
        ),
      );
    }
  }
  for (const key of routed.keys()) {
    if (!manifestByKey.has(key)) {
      problems.push(
        problem(
          `routes['${key}']`,
          `매니페스트에 없는 화면 '${key}' 의 라우트가 있다. **감사가 못 보는 URL** 이 생긴 것이고, ` +
            `그 URL 은 권한 판정을 아무도 선언하지 않은 채 열린다.`,
        ),
      );
    }
  }

  /* ── 내비 ⊆ 라우트 ── */
  for (const key of navScreenKeys) {
    if (!routed.has(key)) {
      problems.push(
        problem(
          `nav['${key}']`,
          `내비에 뜨는 화면 '${key}' 에 라우트가 없다 — 구 시스템의 죽은 링크 6개가 이 형태였다. ` +
            `deriveNav 와 buildRoutes 가 같은 선언을 다르게 읽었다.`,
        ),
      );
    }
  }

  return problems;
}
